import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { PermissionGuard } from "../../components/PermissionGuard";
import { QuickBookService } from "./Service";
import QuickBookRequestDetails from "./QuickBookRequestDetails";
import { quickBookModeLabel, quickBookStatusLabel } from "./display";

const REQUEST_STATUSES = [
  "PAYMENT_PENDING",
  "PRIMARY_EXPERT_PENDING",
  "OPS_ACTION_REQUIRED",
  "ASSIGNED",
  "CANCELLED",
  "FAILED",
];

const DEFAULT_FILTERS = {
  search: "",
  status: "ALL",
  mode: "",
  paymentState: "",
  from: "",
  to: "",
};

const ok = (response) => response?.success === 1 || response?.success === "1";

export const operationQueueQuery = (values) => ({
  search: String(values.search || "").trim(),
  status: values.status === "ALL" ? "" : values.status || "",
  mode: values.mode || "",
  paymentState: values.paymentState || "",
  from: values.from || "",
  to: values.to || "",
  sort: "newest",
});

export const formatQuickBookTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(date);
};

const ageLabel = (value, now) => {
  if (!value) return "—";
  const milliseconds = now - new Date(value).getTime();
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return "—";
  const minutes = Math.floor(milliseconds / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ${minutes % 60}m` : `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const statusColor = (status) => ({
  OPS_ACTION_REQUIRED: "warning",
  ASSIGNED: "success",
  ACCEPTED: "success",
  OPS_CONFIRMED: "success",
  CANCELLED: "default",
  FAILED: "error",
  TIMED_OUT: "error",
  DECLINED: "warning",
  PAYMENT_PENDING: "info",
  PRIMARY_EXPERT_PENDING: "info",
}[status] || "default");

const EmptyRows = () => (
  <Box sx={{ display: "grid", height: "100%", placeItems: "center", p: 2 }}>
    <Typography color="text.secondary">No Quick Book requests match these filters.</Typography>
  </Box>
);

export default function QuickBookOperations() {
  const dispatch = useDispatch();
  const requestLoadId = useRef(0);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [serverNow, setServerNow] = useState(() => Date.now());
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRequestNumber = searchParams.get('requestNumber');
  const setSelectedRequestNumber = useCallback((requestNumber) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (requestNumber) next.set('requestNumber', requestNumber);
      else next.delete('requestNumber');
      return next;
    });
  }, [setSearchParams]);
  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: DEFAULT_FILTERS });
  const from = watch("from");
  const to = watch("to");

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "quick-book-operations" });
  }, [dispatch]);

  const fetchRequests = useCallback(async () => {
    const loadId = ++requestLoadId.current;
    setLoading(true);
    setError("");
    const response = await QuickBookService.operationRequests({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      ...operationQueueQuery(filters),
    });
    if (loadId !== requestLoadId.current) return;
    if (ok(response)) {
      setRows(response.data?.items || []);
      setRowCount(response.data?.pagination?.totalItems || 0);
      const responseTime = new Date(response.data?.serverTime || 0).getTime();
      setServerNow(Number.isFinite(responseTime) && responseTime > 0 ? responseTime : Date.now());
    } else {
      setRows([]);
      setRowCount(0);
      setError(response?.error?.code === "QUICK_BOOK_DISABLED"
        ? "Quick Book is currently disabled."
        : response?.message || "The Operations queue could not be loaded.");
    }
    setLoading(false);
  }, [filters, paginationModel]);

  useEffect(() => {
    fetchRequests();
    return () => { requestLoadId.current += 1; };
  }, [fetchRequests]);

  const columns = useMemo(() => [
    {
      field: "requestNumber",
      headerName: "Request / age",
      minWidth: 190,
      flex: 1,
      renderCell: ({ row }) => (
        <Box>
          <Typography color="primary.main" sx={{ fontSize: ".78rem", fontWeight: 700 }}>{row.requestNumber}</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography color="text.secondary" sx={{ fontSize: ".68rem" }}>Created {ageLabel(row.createdAt, serverNow)} ago</Typography>
            <Chip size="small" variant="outlined" label={`v${row.version}`} sx={{ height: 19, fontSize: ".62rem" }} />
          </Stack>
        </Box>
      ),
    },
    {
      field: "title",
      headerName: "Service",
      minWidth: 220,
      flex: 1.2,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: ".78rem", fontWeight: 700 }}>{row.title || "Quick Book service"}</Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            <Chip size="small" variant="outlined" label={quickBookModeLabel(row.mode)} sx={{ height: 21, fontSize: ".65rem" }} />
            <Chip size="small" label={`Qty ${row.quantity}`} sx={{ height: 21, fontSize: ".65rem" }} />
          </Stack>
        </Box>
      ),
    },
    {
      field: "scheduledStartAt",
      headerName: "Schedule / area",
      minWidth: 210,
      flex: 1,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: ".72rem", fontWeight: 600 }}>{formatQuickBookTime(row.scheduledStartAt)}</Typography>
          <Typography color="text.secondary" sx={{ fontSize: ".68rem", mt: 0.4 }}>{row.area || "Area unavailable"}</Typography>
        </Box>
      ),
    },
    {
      field: "primaryAttempt",
      headerName: "Primary result",
      minWidth: 190,
      sortable: false,
      renderCell: ({ value }) => value ? (
        <Box>
          <Chip size="small" color={statusColor(value.status)} label={quickBookStatusLabel(value.status)} />
          <Typography color="text.secondary" sx={{ fontSize: ".65rem", mt: 0.4 }}>
            {value.declineReasonCode || "No reason"} · {formatQuickBookTime(value.respondedAt)}
          </Typography>
        </Box>
      ) : <Typography color="text.secondary">—</Typography>,
    },
    {
      field: "payment",
      headerName: "Payment / refund",
      minWidth: 185,
      sortable: false,
      renderCell: ({ value }) => (
        <PermissionGuard feature="booking-payment" permission="canView" fallback={<Typography color="text.secondary" sx={{ fontSize: ".7rem" }}>Restricted</Typography>}>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            <Chip size="small" variant="outlined" label={value?.choice || "UNKNOWN"} />
            <Chip size="small" color={value?.state === "CAPTURED" ? "success" : value?.state === "FAILED" ? "error" : "default"} label={quickBookStatusLabel(value?.state)} />
            {value?.refundState && value.refundState !== "NONE" && <Chip size="small" color="warning" label={quickBookStatusLabel(value.refundState)} />}
          </Stack>
        </PermissionGuard>
      ),
    },
    {
      field: "slaDueAt",
      headerName: "Status / SLA",
      minWidth: 230,
      renderCell: ({ row }) => {
        const overdue = row.status === "OPS_ACTION_REQUIRED" && row.slaDueAt && new Date(row.slaDueAt).getTime() < serverNow;
        return (
          <Box sx={{ px: 1, py: 0.75, borderRadius: 2, bgcolor: overdue ? "#fef2f2" : row.status === "ASSIGNED" ? "#ecfdf3" : "grey.100" }}>
            <Chip size="small" color={statusColor(row.status)} label={quickBookStatusLabel(row.status)} />
            <Typography color={overdue ? "error.main" : "text.secondary"} sx={{ fontSize: ".65rem", mt: 0.4 }}>
              {row.slaDueAt ? `${overdue ? "Overdue" : "Due"} ${formatQuickBookTime(row.slaDueAt)}` : "SLA unavailable"}
            </Typography>
            {row.assignedOperator?.actorId && <Typography color="text.secondary" sx={{ fontSize: ".65rem" }}>Admin #{row.assignedOperator.actorId}</Typography>}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Open request details">
          <IconButton aria-label={`Open ${row.requestNumber}`} size="small" color="primary" onClick={() => setSelectedRequestNumber(row.requestNumber)} sx={{ bgcolor: "#eff6ff", "&:hover": { bgcolor: "#dbeafe" } }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [serverNow, setSelectedRequestNumber]);

  const applyFilters = (values) => {
    if (values.from && values.to && values.from > values.to) return;
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFilters(operationQueueQuery(values));
  };

  const clearFilters = () => {
    reset(DEFAULT_FILTERS);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFilters(DEFAULT_FILTERS);
  };

  const closeDetails = useCallback(() => setSelectedRequestNumber(null), [setSelectedRequestNumber]);

  const invalidDateRange = Boolean(from && to && from > to);

  return (
    <>
      <PageHeaderLayout headerTitle="Quick Book Operations">
        <Tooltip title="Refresh queue">
          <span>
            <IconButton aria-label="Refresh Operations queue" onClick={fetchRequests} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </PageHeaderLayout>

      <Card component="form" variant="outlined" onSubmit={handleSubmit(applyFilters)} sx={{ mb: 1.5, p: 1.5, borderRadius: 2, boxShadow: "0 4px 16px rgba(15, 23, 42, 0.05)" }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={4}>
            <Controller name="search" control={control} render={({ field }) => <TextField {...field} fullWidth size="small" label="Request number or service" inputProps={{ maxLength: 100 }} />} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Controller name="status" control={control} render={({ field }) => (
              <TextField {...field} select fullWidth size="small" label="Status">
                <MenuItem value="ALL">All</MenuItem>
                {REQUEST_STATUSES.map((status) => <MenuItem key={status} value={status}>{quickBookStatusLabel(status)}</MenuItem>)}
              </TextField>
            )} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Controller name="mode" control={control} render={({ field }) => (
              <TextField {...field} select fullWidth size="small" label="Mode">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="FIXED_SERVICE">Fixed service</MenuItem>
                <MenuItem value="INSPECTION_FIRST">Inspection</MenuItem>
              </TextField>
            )} />
          </Grid>
          <PermissionGuard feature="booking-payment" permission="canView">
            <Grid item xs={6} sm={4} md={2}>
              <Controller name="paymentState" control={control} render={({ field }) => (
                <TextField {...field} select fullWidth size="small" label="Payment">
                  <MenuItem value="">All</MenuItem>
                  {['PENDING', 'CAPTURED', 'FAILED'].map((state) => <MenuItem key={state} value={state}>{quickBookStatusLabel(state)}</MenuItem>)}
                </TextField>
              )} />
            </Grid>
          </PermissionGuard>
          <Grid item xs={6} sm={4} md={2}>
            <Controller name="from" control={control} render={({ field }) => <TextField {...field} type="date" fullWidth size="small" label="From" InputLabelProps={{ shrink: true }} />} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Controller name="to" control={control} render={({ field }) => <TextField {...field} type="date" fullWidth size="small" label="To" InputLabelProps={{ shrink: true }} />} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" size="small" startIcon={<SearchIcon />} disabled={invalidDateRange} fullWidth>Search</Button>
              <Button type="button" variant="outlined" size="small" onClick={clearFilters} fullWidth>Reset</Button>
            </Stack>
          </Grid>
          {invalidDateRange && <Grid item xs={12}><Alert severity="warning">The From date must be on or before the To date.</Alert></Grid>}
        </Grid>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Card variant="outlined" sx={{ width: "100%", overflowX: "auto", borderRadius: 2, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)" }}>
        <Box sx={{ minWidth: 1320 }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            getRowId={(row) => row.requestNumber}
            getRowHeight={() => "auto"}
            getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? "operation-row-even" : "operation-row-odd"}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            disableRowSelectionOnClick
            slots={{ noRowsOverlay: EmptyRows }}
            sx={{ border: 0, "& .MuiDataGrid-columnHeaders": { bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }, "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 }, "& .MuiDataGrid-cell": { minHeight: 76, alignItems: "center", py: 1.25, borderColor: "grey.200" }, "& .operation-row-even": { bgcolor: "common.white" }, "& .operation-row-odd": { bgcolor: "grey.50" }, "& .MuiDataGrid-row:hover": { bgcolor: "#eff6ff" }, "& .MuiDataGrid-footerContainer": { bgcolor: "grey.50" } }}
          />
        </Box>
      </Card>

      <QuickBookRequestDetails
        requestNumber={selectedRequestNumber}
        open={Boolean(selectedRequestNumber)}
        onClose={closeDetails}
        onChanged={fetchRequests}
      />
    </>
  );
}
